import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { USER } from 'sh-www/models/user';
import { trackPageViewFromTransition } from 'sh-www/utils/datalayer';
import BuyerOnlyRouteMixin from 'sh-www/mixins/buyer-only-route-mixin';
import SellerOnlyRouteMixin from 'sh-www/mixins/seller-only-route-mixin';

const SELLER_QUERY_PARAMS = [
  'sort',
  'secondary_sort',
  'place_id',
  'minimum_day_rate',
  'remote',
  'has_no_fees',
  'covers_expenses',
  'user_offered_services',
].reduce((accum, param) => {
  accum[param] = { refreshModel: true, replace: true };
  return accum;
}, { });

/**
 * @param {string} path
 * @return {Route}
 */
export const sellerRouteFactory = (path, navItemName) => {
  return class SellerPostingsViewRoute extends Route.extend(SellerOnlyRouteMixin) {
    @service('commands/location-commands') locationCommandService;

    get controllerName() {
      return path;
    }

    get queryParams() {
      return SELLER_QUERY_PARAMS;
    }

    get templateName() {
      return 'postings.seller-results';
    }

    /**
     * dummy model required, otherwise `refreshModel: true` will not trigger lifecycle
     * methods on param update
     */
    async model({ place_id }) {
      const modelData = {};

      // Resolve location model so we can fill the input
      const locationPromise = place_id
        ? this.locationCommandService.createLocationFromGooglePlace(place_id)
        : Promise.resolve(null);

      modelData.location = await locationPromise;

      return modelData;
    }

    /** @inheritDoc */
    setupController(controller, model, transition) {
      super.setupController(...arguments);

      const { queryParams } = transition.to;

      this.controllerFor('postings.find-work').set('activeNavItem', navItemName);
      controller.set('formattedAddressForPlaceId', model.location?.formattedAddress ?? '');

      if (!controller.modelIsValid(queryParams)) {
        controller.reloadModel.perform(queryParams);
      }
    }

    activate(transition) {
      super.activate(...arguments);
      trackPageViewFromTransition(this, transition, path);
    }
  };
};

/**
 * @param {closed|draft|live|in_review} assignmentStatus
 * @param {string} path
 * @returns {Ember.Route}
 */
export const buyerRouteFactory = (assignmentStatus, path) => {
  return class PostingsIndexFactoryRoute extends Route.extend(BuyerOnlyRouteMixin) {
    /**
     * State manager for this route
     * @type {Service}
     */
    @service('commands/company-commands') companyCommandService;
    @service('commands/company-team-commands') companyTeamCommandService;
    @service router;
    @service session;
    @service shData;
    @service('state-managers/routes/postings') stateManager;
    @service('states/routes/postings') state;

    /** @inheritDoc */
    get controllerName() {
      return 'postings/buyer';
    }

    /** @inheritDoc */
    get templateName() {
      return 'postings/buyer';
    }

    /** @inheritDoc */
    model() {
      this.controllerFor('postings/buyer').resetController();

      const loadAssignmentDashboardList = this.stateManager.loadAssignmentDashboardList(assignmentStatus);
      const companyTeams = this.companyTeamCommandService.loadCompanyTeams(
        this.session.currentUser.company.id,
        ['company-team-memberships.company-membership.user'],
      );
      const companyWithMembers = this.companyCommandService.loadCompany(this.session.currentUser.company.id, [
        'company-memberships.user',
        'company-memberships.company-team-memberships',
      ]);

      return Promise.all([loadAssignmentDashboardList, companyTeams, companyWithMembers]).then(([loadAssignmentDashboardList, companyTeams, companyWithMembers]) => {
        this.shData.pushResponseToStore(companyTeams);

        return {
          companyTeams: this.shData.getModelArrayFromResponse(companyTeams, 'companyTeam'),
          companyMemberships: companyWithMembers.companyMemberships.toArray(),
        };
      });
    }

    /** @inheritDoc */
    activate(transition) {
      super.activate(...arguments);
      trackPageViewFromTransition(this, transition, path);
    }

    /** @inheritDoc */
    _redirectUnauthorizedUser() {
      this.router.transitionTo('postings');
    }
  }
}

/** @module */
class PostingsIndexRoute extends Route {
  @service router;
  @service session;

  /** @inheritDoc */
  redirect() {
    switch (this.session.currentUser.type) {
      case USER.TYPES.ADMIN: return this.router.replaceWith(EMBER_ROUTE_NAMES.ADMIN__POSTINGS);
      case USER.TYPES.BUYER: return this.router.replaceWith(EMBER_ROUTE_NAMES.POSTINGS__LIVE);
      case USER.TYPES.SELLER: return this.router.replaceWith(EMBER_ROUTE_NAMES.POSTINGS__FIND_WORK);
      default: throw new Error('PostingsIndexRoute::redirect() - no route defined for user type');
    }
  }
}

export default PostingsIndexRoute;
